// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { CONTROLLED_TESTING_OUTCOME_DECLARATION_KEYS, CONTROLLED_TESTING_OUTCOME_FILINGS, } from '../types/controlled-testing.js';
/**
 * Sprint 62.51.0 (CL-3) — the organisation outcome filings a procedure offers,
 * and the one shape check the pack validator and the runtime reader share (the
 * `procedure-lifecycle.ts` rule: two copies of a rule disagree about one record
 * eventually).
 *
 * ## The declaration
 *
 * `outcome_filings` on a procedure is a list drawn from
 * `CONTROLLED_TESTING_OUTCOME_FILINGS` (`stop`, `completion`), without
 * repeats. **Absent means both**: every pack installed before the key existed
 * keeps the stop and completion filings it had. An empty list means the
 * procedure offers neither: its outcome is recorded somewhere else (a register
 * that holds the authority's own acts), and the case says where.
 *
 * ## R-46
 *
 * Nothing here names an instrument or a procedure. The members are the case
 * core's own two filings.
 */
const isObject = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
/** Each problem as a sentence; empty when the declaration is absent or well-formed. */
export function findProcedureOutcomeFilingErrors(procedure) {
    if (!isObject(procedure))
        return [];
    const key = CONTROLLED_TESTING_OUTCOME_DECLARATION_KEYS.outcomeFilings;
    if (!(key in procedure))
        return [];
    const value = procedure[key];
    if (!Array.isArray(value))
        return [`${key} must be an array`];
    const errors = [];
    const seen = new Set();
    value.forEach((member, i) => {
        if (typeof member !== 'string' ||
            !CONTROLLED_TESTING_OUTCOME_FILINGS.includes(member)) {
            errors.push(`${key}[${i}] must be one of ${CONTROLLED_TESTING_OUTCOME_FILINGS.join(', ')}; got ${JSON.stringify(member)}`);
            return;
        }
        if (seen.has(member))
            errors.push(`${key}[${i}] repeats ${member}`);
        seen.add(member);
    });
    return errors;
}
/**
 * The filings a procedure offers, read from the installed record. Absent (or a
 * value that is not a list) reads as both, so no installed pack loses a filing
 * it had; an unknown member is ignored (the validator refuses it at pack time).
 */
export function readProcedureOutcomeFilings(procedure) {
    const key = CONTROLLED_TESTING_OUTCOME_DECLARATION_KEYS.outcomeFilings;
    if (!isObject(procedure) || !Array.isArray(procedure[key])) {
        return { declared: false, offered: [...CONTROLLED_TESTING_OUTCOME_FILINGS] };
    }
    const listed = new Set(procedure[key].filter((m) => typeof m === 'string'));
    return {
        declared: true,
        offered: CONTROLLED_TESTING_OUTCOME_FILINGS.filter((f) => listed.has(f)),
    };
}
/**
 * The forms a case may open on for a procedure — the one rule the open route and
 * the open form share, so the form never disables a procedure the route accepts.
 *
 * A procedure's prescribed application forms (`application_form_ids`) when it
 * declares the key. Otherwise, a generic documentary procedure that expressly
 * offers no outcome filing (`outcome_filings: []`, its outcome kept in a register
 * of the authority's own acts) opens on its declared record forms
 * (`related_form_ids`). Anything else declares no form, and no case opens on it.
 */
export function readProcedureCaseForms(procedure) {
    if (!isObject(procedure))
        return [];
    const strings = (v) => Array.isArray(v) ? v.filter((f) => typeof f === 'string') : [];
    if (Object.hasOwn(procedure, 'application_form_ids')) {
        return strings(procedure.application_form_ids);
    }
    const outcomes = readProcedureOutcomeFilings(procedure);
    return outcomes.declared && outcomes.offered.length === 0
        ? strings(procedure.related_form_ids)
        : [];
}
//# sourceMappingURL=procedure-outcome-filings.js.map